import { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, ArrowLeftRight, Wallet, TrendingUp, PiggyBank, HeartPulse, House, Car, ShieldCheck, Plane, Briefcase, ArrowRight } from "lucide-react";
import { SERVICES } from "@/content/site";
import { Reveal } from "@/components/Reveal";

const ICONS = { KeyRound, ArrowLeftRight, Wallet, TrendingUp, PiggyBank, HeartPulse, House, Car, ShieldCheck, Plane, Briefcase };

const GROUPS = [
  { key: "uvery", label: "Hypotéky a úvěry" },
  { key: "investice", label: "Investice a důchod" },
  { key: "pojisteni", label: "Pojištění" },
];

const ServiceCard = ({ service }) => {
  const Icon = ICONS[service.icon];
  return (
    <article
      data-testid={`service-card-${service.id}`}
      className="bg-white border border-[#F0F0F0] p-8 md:p-10 hover:border-[#B69A58] transition-colors duration-300 flex flex-col items-start gap-6 h-full"
    >
      <Icon className="h-8 w-8 text-[#B69A58]" strokeWidth={1} aria-hidden="true" />
      <h3 className="font-display text-2xl leading-snug text-[#252525]">
        <Link
          to={`/pojisteni/${service.id}`}
          data-testid={`service-link-${service.id}`}
          className="hover:text-[#B69A58] transition-colors duration-300"
        >
          {service.title}
        </Link>
      </h3>
      <p className="text-sm md:text-base text-[#4A4A4A] leading-relaxed">{service.text}</p>
      <a
        href="#termin"
        data-testid={`service-cta-${service.id}`}
        className="group mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#252525] hover:text-[#B69A58] transition-colors duration-300 pt-2"
      >
        Probrat tuto oblast
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
      </a>
    </article>
  );
};

export const Services = () => {
  const [activeGroup, setActiveGroup] = useState("uvery");
  const mobileItems = SERVICES.filter((s) => (s.group || "pojisteni") === activeGroup);

  return (
    <section id="pojisteni" data-testid="services-section" className="py-24 md:py-32 border-t border-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Služby
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#252525] mt-6 max-w-2xl">
            S čím vám mohu pomoci
          </h2>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-6 max-w-2xl">
            Od hypotéky přes investice po pojištění. Každou oblast řeším tak,
            abyste přesně věděli, co máte sjednané — a proč.
          </p>
        </Reveal>

        {/* Mobil: kompaktní přepínač skupin + seznam */}
        <div className="mt-10 lg:hidden" data-testid="services-mobile">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Skupiny služeb">
            {GROUPS.map((group) => (
              <button
                key={group.key}
                type="button"
                role="tab"
                aria-selected={activeGroup === group.key}
                data-testid={`services-tab-${group.key}`}
                onClick={() => setActiveGroup(group.key)}
                className={`border border-[#B69A58] px-5 py-3 text-xs uppercase tracking-widest font-medium transition-colors duration-300 ${
                  activeGroup === group.key
                    ? "bg-[#B69A58] text-white"
                    : "bg-white text-[#252525] hover:text-[#B69A58]"
                }`}
              >
                {group.label}
              </button>
            ))}
          </div>
          <div className="mt-6 border-t border-[#F0F0F0]" data-testid="services-mobile-list">
            {mobileItems.map((service) => (
              <Link
                key={service.id}
                to={`/pojisteni/${service.id}`}
                data-testid={`service-mobile-link-${service.id}`}
                className="group flex items-center justify-between gap-4 py-5 border-b border-[#F0F0F0]"
              >
                <div className="min-w-0">
                  <p className="font-display text-lg leading-snug text-[#252525] group-hover:text-[#B69A58] transition-colors duration-300">
                    {service.title}
                  </p>
                  <p className="text-sm text-[#4A4A4A] leading-relaxed mt-1 line-clamp-2">{service.text}</p>
                </div>
                <ArrowRight className="h-5 w-5 shrink-0 text-[#B69A58] transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>

        {/* Desktop: plná mřížka karet */}
        <div className="hidden lg:block">
          {GROUPS.map((group) => {
            const items = SERVICES.filter((s) => (s.group || "pojisteni") === group.key);
            if (items.length === 0) return null;
            return (
              <div key={group.key} className="mt-16 first:mt-16" data-testid={`services-group-${group.key}`}>
                <Reveal>
                  <h3 className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#252525] font-medium">
                    <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
                    {group.label}
                  </h3>
                </Reveal>
                <div className="grid grid-cols-3 gap-x-8 gap-y-12 mt-10">
                  {items.map((service, i) => (
                    <Reveal key={service.id} delay={i * 0.05}>
                      <ServiceCard service={service} />
                    </Reveal>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: výrazný blok s odkazy na interaktivní nástroje */}
        <Reveal>
          <div className="hidden lg:block mt-20 pt-14 border-t border-[#F0F0F0] text-center" data-testid="services-tools-strip">
            <p className="flex items-center justify-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
              <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
              Kalkulačky a výpočty
              <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            </p>
            <p className="text-sm text-[#4A4A4A] mt-4">
              Interaktivní nástroje najdete přímo u jednotlivých služeb
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <Link
                to="/pojisteni/hypoteky"
                data-testid="services-tool-calc"
                className="group inline-flex items-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
              >
                Hypoteční kalkulačka
                <ArrowRight className="h-4 w-4 text-[#B69A58] group-hover:text-white transition-all duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              <Link
                to="/pojisteni/refinancovani"
                data-testid="services-tool-fixace"
                className="group inline-flex items-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
              >
                Hlídání konce fixace
                <ArrowRight className="h-4 w-4 text-[#B69A58] group-hover:text-white transition-all duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              <Link
                to="/pojisteni/vozidla"
                data-testid="services-tool-vozidla"
                className="group inline-flex items-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
              >
                Hlídání povinného ručení
                <ArrowRight className="h-4 w-4 text-[#B69A58] group-hover:text-white transition-all duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
